import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { type SeedStripe } from "../../utils/seedFlow";

export interface ManualStripePreview {
  productId: string;
  color: string;
  stripes: SeedStripe[];
  selectedIds: string[];
  disabledIds: string[];
  savedColors?: Record<string, string>;
  highlightedId?: string | null;
}

interface StripeManualSelectionContextValue {
  preview: ManualStripePreview | null;
  setPreview: (preview: ManualStripePreview | null) => void;
  registerToggle: (handler: ((id: string) => void) | null) => void;
  toggleStripe: (id: string) => void;
}

const StripeManualSelectionContext =
  createContext<StripeManualSelectionContextValue | null>(null);

interface StripeManualSelectionProviderProps {
  children: ReactNode;
}

export const StripeManualSelectionProvider = ({
  children,
}: StripeManualSelectionProviderProps): JSX.Element => {
  const [preview, setPreview] = useState<ManualStripePreview | null>(null);
  const toggleRef = useRef<((id: string) => void) | null>(null);

  const registerToggle = useCallback(
    (handler: ((id: string) => void) | null): void => {
      toggleRef.current = handler;
    },
    []
  );

  const toggleStripe = useCallback((id: string): void => {
    toggleRef.current?.(id);
  }, []);

  const value = useMemo<StripeManualSelectionContextValue>(
    () => ({
      preview,
      setPreview,
      registerToggle,
      toggleStripe,
    }),
    [preview, registerToggle, toggleStripe]
  );

  return (
    <StripeManualSelectionContext.Provider value={value}>
      {children}
    </StripeManualSelectionContext.Provider>
  );
};

export const useStripeManualSelection =
  (): StripeManualSelectionContextValue => {
    const context = useContext(StripeManualSelectionContext);

    if (!context) {
      throw new Error(
        "Stripe manual selection requires StripeManualSelectionProvider."
      );
    }

    return context;
  };
